import { tv } from "@ui/lib/tv";
import { ScrollArea as Primitive } from "@ui/primitives/ScrollArea";
import type { ComponentProps, ReactNode, Ref } from "react";

const scrollArea = tv({
  slots: {
    // Sizing lives on the root so callers size one element, but the viewport is
    // what actually scrolls and so has to take that size on. A percentage height
    // can't do it — it has nothing to resolve against when the root is itself
    // flex-sized — so the root is a flex column the viewport shrinks inside,
    // which lands the same whether the root has a height or only a cap.
    root: "flex min-h-0 flex-col",
    viewport: "min-h-0 flex-auto overscroll-contain",
    // The lane is anchored to the container's inline end, so a 20px lane with the
    // thumb flush to its inline start leaves the thumb 12px inside that edge —
    // the Figma "scroll" placement — without overriding the inline positioning
    // the primitive sets.
    //
    // The lane is painted over the content, unlike the layout-consuming native bar
    // it replaces, so it stays out of hit testing: the track would otherwise
    // swallow clicks on the trailing edge of every row beneath it. Only the thumb
    // takes pointers, which costs click-the-track-to-page and keeps drag.
    scrollbar: "pointer-events-none flex w-5 justify-start",
    // A bare background disappears in forced-colors, where the native bar this
    // replaces would have been painted by the UA in system colours.
    thumb: "pointer-events-auto w-2 rounded-full bg-neutral-100 forced-colors:bg-[CanvasText]",
  },
  variants: {
    // How far the rail sits from the container's inline end.
    inset: {
      card: {},
      none: { scrollbar: "w-2" },
    },
  },
  defaultVariants: { inset: "card" },
});

export interface ScrollAreaProps {
  children?: ReactNode;
  /** Sizes the scroller — put `max-h-*` and any bleed margins here. */
  className?: string;
  /** Padding and layout for the scrolling element itself. */
  viewportClassName?: string;
  /**
   * Replaces the viewport element, so a component that must own the scroll
   * container — a React Aria `ListBox`, which scrolls its focused option into
   * view through its own ref — stays the scroller.
   *
   * A collection keeps its rows as the render element's own children, not this
   * component's: that is what lets `ListBox` infer its item type from `items`.
   * Children on the render element win over any passed here.
   */
  render?: ComponentProps<typeof Primitive.Viewport>["render"];
  /** The scrolling element, for callers that observe scroll state (e.g. a bottom sentinel). */
  viewportRef?: Ref<HTMLDivElement>;
  inset?: "card" | "none";
}

/**
 * Scrolling region with the design system's rail: an 8px neutral-100 pill held
 * 12px inside the container's edge, identical in every engine because the thumb
 * is an element rather than a native scrollbar (which no engine but Blink and
 * WebKit lets you size, round, or inset).
 *
 * The rail mounts only while the content overflows, so a list that fits shows
 * nothing.
 */
export function ScrollArea({ children, className, viewportClassName, render, viewportRef, inset }: ScrollAreaProps) {
  const styles = scrollArea({ inset });
  return (
    <Primitive.Root className={styles.root({ className })}>
      <Primitive.Viewport
        className={styles.viewport({ className: viewportClassName })}
        ref={viewportRef}
        render={render}
      >
        {children}
      </Primitive.Viewport>
      <Primitive.Scrollbar className={styles.scrollbar()}>
        <Primitive.Thumb className={styles.thumb()} />
      </Primitive.Scrollbar>
    </Primitive.Root>
  );
}
